import React, { useState, useCallback } from 'react';
import { Table, Badge, Button, Card, Form, Spinner, Alert } from 'react-bootstrap';
import { useQuery } from '@tanstack/react-query';
import { useSelector } from 'react-redux';
import { RootState } from '../store';
import {
  getInvoicesInvoiceIdAttachments,
  getInvoicesInvoiceIdAttachmentsAttachmentId,
  patchInvoicesId,
} from '../api/generated/arrayscapeGamingDashboardAPI';
import type { Invoice, InvoiceAttachment, Payment } from '../api/generated/model';
import AttachmentViewer from './common/AttachmentViewer';
import { DataTable } from './common/DataTable';
import type { ColumnConfig } from './common/DataTable/types';

const formatCurrency = (amount?: string): string => {
  if (!amount) return '';
  const num = parseFloat(amount);
  if (isNaN(num)) return amount;
  return new Intl.NumberFormat('en-US', { style: 'currency', currency: 'USD', minimumFractionDigits: 2 }).format(num);
};

const formatDate = (dateStr?: string): string => {
  if (!dateStr) return '';
  try {
    const d = new Date(dateStr);
    if (isNaN(d.getTime()) || d.getFullYear() < 1900) return '';
    return d.toLocaleDateString('en-US', { month: 'numeric', day: 'numeric', year: 'numeric' });
  } catch {
    return '';
  }
};

export const statusBadge = (status?: string) => {
  const variants: Record<string, string> = {
    NotDue: 'primary', OverDue: 'danger', PaidInFull: 'success', OverPaid: 'warning',
  };
  const labels: Record<string, string> = {
    NotDue: 'Not Due', OverDue: 'Overdue', PaidInFull: 'Paid', OverPaid: 'Overpaid',
  };
  return <Badge bg={variants[status || ''] || 'secondary'}>{labels[status || ''] || status || 'Unknown'}</Badge>;
};

export const qboInvoiceUrl = (invoice: Invoice): string | null => {
  if (!invoice.src_id || !invoice.id) return null;
  const qbInvoiceId = invoice.id.split('-').slice(1).join('-');
  return `https://qbo.intuit.com/login?deeplinkcompanyid=${invoice.src_id}&pagereq=app/invoice?txnId=${qbInvoiceId}`;
};

// qboPaymentUrl returns a deep link into the QuickBooks Online "Receive
// Payment" page for the given Payment. Returns null when the payment isn't
// from QuickBooks or is missing the composite id parts.
export const qboPaymentUrl = (payment: Payment): string | null => {
  if (payment.src_type !== 'quickbooks') return null;
  if (!payment.src_id || !payment.id) return null;
  // payment.id is the composite "<src_id>:<inner_id>"; strip the prefix.
  const innerID = payment.id.startsWith(payment.src_id + ':')
    ? payment.id.slice(payment.src_id.length + 1)
    : payment.id;
  return `https://qbo.intuit.com/login?deeplinkcompanyid=${payment.src_id}&pagereq=app/recvpayment?txnId=${innerID}`;
};

interface InvoiceDetailContentProps {
  invoice: Invoice;
  onInvoiceUpdated?: (invoice: Invoice) => void;
}

const InvoiceDetailContent: React.FC<InvoiceDetailContentProps> = ({ invoice, onInvoiceUpdated }) => {
  const { user } = useSelector((state: RootState) => state.auth);
  const lines = invoice.lines || [];
  const payments = invoice.payments || [];
  const hasDiscount = invoice.discount_amount && parseFloat(invoice.discount_amount) !== 0;
  const canApprove = user?.role === 'admin' || user?.role === 'finance' || user?.role === 'vp_sales' || user?.role === 'sales_manager';

  const handleApprovalToggle = async (field: string, value: boolean | string) => {
    if (!invoice.id) return;
    try {
      const updated = await patchInvoicesId(invoice.id, [
        { op: 'replace', path: `/${field}`, value },
      ] as any);
      onInvoiceUpdated?.(updated as Invoice);
    } catch (err) {
      console.error('Failed to update approval:', err);
    }
  };

  const handleLineDeliveryToggle = async (lineId: string, delivered: boolean) => {
    if (!invoice.id) return;
    try {
      const updated = await patchInvoicesId(invoice.id, [
        { op: 'replace', path: `/lines/${lineId}/delivered`, value: delivered },
      ] as any);
      onInvoiceUpdated?.(updated as Invoice);
    } catch (err) {
      console.error('Failed to update delivery:', err);
    }
  };

  // Measure widest description line (including any rendered error message beneath it)
  const descriptionWidth = React.useMemo(() => {
    const canvas = document.createElement('canvas');
    const ctx = canvas.getContext('2d');
    if (!ctx) return 400;
    ctx.font = getComputedStyle(document.body).font || '14px system-ui';
    let widest = 0;
    for (const line of lines) {
      const desc = line.description || '';
      for (const textLine of desc.split('\n')) {
        const w = ctx.measureText(textLine).width;
        if (w > widest) widest = w;
      }
      if (line.error) {
        const w = ctx.measureText(line.error).width;
        if (w > widest) widest = w;
      }
    }
    // Add padding (cell padding + some breathing room)
    return Math.min(Math.max(Math.ceil(widest) + 24, 200), 572);
  }, [lines]);

  const lineColumns: ColumnConfig<any>[] = [
    { key: 'line_num', label: '#', width: 35 },
    {
      key: 'type', label: 'Type', width: 115,
      renderCell: (line) => <span style={{ whiteSpace: 'normal', wordBreak: 'break-word', display: 'block', overflow: 'visible' }}>{line.program_type
        ? <Badge bg="info">{line.program_type}</Badge>
        : (line.item_name || line.sales_item || '')
      }</span>,
    },
    {
      key: 'description', label: 'Description', width: descriptionWidth,
      renderCell: (line) => (
        <div>
          <span style={{ whiteSpace: 'pre-line' }}>{line.description || ''}</span>
          {line.error && (
            <div
              className="text-danger small mt-1"
              style={{ whiteSpace: 'normal', wordBreak: 'break-word' }}
            >
              <i className="bi bi-exclamation-triangle me-1"></i>
              {line.error}
            </div>
          )}
        </div>
      ),
    },
    {
      key: 'quantity', label: 'Qty', width: 60,
      renderCell: (line) => <span className="d-block text-end">{line.sales_item_quantity || ''}</span>,
    },
    {
      key: 'unit_price', label: 'Unit Price', width: 100,
      renderCell: (line) => <span className="d-block text-end">{formatCurrency(line.sales_item_unit_price)}</span>,
    },
    {
      key: 'amount', label: 'Amount', width: 100,
      renderCell: (line) => <span className="d-block text-end">{formatCurrency(line.amount)}</span>,
    },
    {
      // Wrap the checkbox in a full-cell <label> so the entire 80px-wide cell
      // is a click target — the small native checkbox alone is hard to hit.
      // Native label semantics propagate the click to the nested input; nothing
      // else on the row reacts to clicks (no row click handler), so wrapping
      // the whole cell is safe.
      key: 'delivered', label: 'Delivered', width: 80,
      renderCell: (line) => line.requires_delivery === false
        ? <div className="text-center"><span className="text-muted">&mdash;</span></div>
        : (
          <label
            className="d-flex align-items-center justify-content-center mb-0 w-100 h-100"
            style={{ cursor: canApprove ? 'pointer' : 'not-allowed', minHeight: '32px' }}
          >
            <Form.Check
              type="checkbox"
              checked={line.delivered || false}
              disabled={!canApprove}
              onChange={(e) => handleLineDeliveryToggle(line.line_id, e.target.checked)}
              className="d-inline-block"
            />
          </label>
        ),
    },
  ];

  // Group attributions by role so we can show the full list (not just the
  // legacy first-parsed scalar). Each entry shows name + amount + source so
  // an admin debugging a commission validation issue can see exactly what
  // the parser pulled and from where (custom_field / line_item / private_note).
  const attributions = invoice.attributions || [];
  const salespeopleAttrs = attributions.filter(a => a.role === 'salesperson');
  const salesLeadsAttrs = attributions.filter(a => a.role === 'sales_lead');
  const renderAttrList = (list: typeof attributions): React.ReactNode => {
    if (list.length === 0) return null;
    // Format an ISO datetime as YYYY-MM-DD without timezone math (commission
    // dates are calendar dates in the business timezone — splitting the
    // string preserves them without a viewer-TZ shift).
    const fmtAnchor = (iso: string): string => iso.split('T')[0];
    return (
      <div>
        {list.map((a, i) => (
          <div key={i}>
            {a.name}
            {a.commission_anchor_date && (
              <span
                className="text-info small ms-1"
                title="Commission anchor date — overrides invoice.booking_date for this sales-lead row's quota lookup + period bucketing. Set via PrivateNote `Sales Lead: <name> @ <date>`."
              >
                · anchored {fmtAnchor(a.commission_anchor_date)}
              </span>
            )}
            {a.amount && <span className="text-muted"> | ${a.amount}</span>}
            {a.source && a.source !== 'custom_field' && (
              <span className="text-muted small ms-1">({a.source})</span>
            )}
          </div>
        ))}
      </div>
    );
  };

  // Route the attribution_errors string to whichever role section it
  // names so the warning sits next to the data it's about. The parser
  // writes role-specific messages ("multiple salesperson attributions
  // ...", "multiple sales_lead attributions ...") and the dual-role
  // case ("user X is attributed as both salesperson and sales_lead").
  // Substring matching covers all three; the dual-role error appears
  // under both columns.
  const attrErr = invoice.attribution_errors || '';
  const attrErrAffectsSalesperson = attrErr.includes('salesperson');
  const attrErrAffectsSalesLead = attrErr.includes('sales_lead');
  const renderAttrError = () => (
    <div className="text-warning small mt-1" title={attrErr}>
      <i className="bi bi-exclamation-triangle-fill me-1" />{attrErr}
    </div>
  );

  return (
    <div className="d-flex flex-column flex-grow-1" style={{ minHeight: 0 }}>
      {/* Metadata row */}
      <div className="d-flex mb-3 flex-shrink-0">
        <div style={{ flex: '1 1 33%' }}>
          {invoice.booking_date && (
            <span><span className="fw-semibold">Booking Date:</span> {formatDate(invoice.booking_date)}</span>
          )}
        </div>
        <div style={{ flex: '1 1 33%' }}>
          {salespeopleAttrs.length > 0 ? (
            <>
              <div className="fw-semibold">Salesperson:</div>
              {renderAttrList(salespeopleAttrs)}
            </>
          ) : invoice.sales_person ? (
            <span><span className="fw-semibold">Salesperson:</span> {invoice.sales_person}</span>
          ) : null}
          {attrErr && attrErrAffectsSalesperson && renderAttrError()}
        </div>
        <div style={{ flex: '1 1 33%' }}>
          {salesLeadsAttrs.length > 0 ? (
            <>
              <div className="fw-semibold">Sales Lead:</div>
              {renderAttrList(salesLeadsAttrs)}
            </>
          ) : invoice.sales_lead ? (
            <span><span className="fw-semibold">Sales Lead:</span> {invoice.sales_lead}</span>
          ) : null}
          {attrErr && attrErrAffectsSalesLead && renderAttrError()}
        </div>
      </div>
      {/* If the attribution_errors string doesn't mention either role
          explicitly (e.g. an "unknown user" warning that names someone
          but not a role), fall back to a plain row at the top so it
          doesn't get lost. */}
      {attrErr && !attrErrAffectsSalesperson && !attrErrAffectsSalesLead && (
        <Alert variant="warning" className="mb-3 flex-shrink-0">
          <i className="bi bi-exclamation-triangle-fill me-2" />
          <span className="fw-semibold">Attribution issue:</span> {attrErr}
        </Alert>
      )}
      {invoice.import_issues && invoice.import_issues.length > 0 && (
        <Alert variant="warning" className="mb-3 flex-shrink-0">
          <div className="d-flex align-items-start">
            <i className="bi bi-exclamation-triangle-fill me-2 mt-1" />
            <div>
              <div className="fw-semibold mb-1">
                {invoice.import_issues.length === 1 ? 'Import issue' : 'Import issues'}
              </div>
              {invoice.import_issues.length === 1 ? (
                <div>{invoice.import_issues[0]}</div>
              ) : (
                <ul className="mb-0">
                  {invoice.import_issues.map((msg, idx) => (
                    <li key={idx}>{msg}</li>
                  ))}
                </ul>
              )}
            </div>
          </div>
        </Alert>
      )}
      {invoice.purchase_order && (
        <div className="mb-3 flex-shrink-0">
          <span><span className="fw-semibold">Purchase Order #:</span> {invoice.purchase_order}</span>
        </div>
      )}

      {/* Memo fields */}
      {invoice.customer_memo && (
        <div className="mb-3 flex-shrink-0">
          <div className="fw-semibold mb-1">Note to Customer</div>
          <div className="p-3 bg-body-tertiary border rounded" style={{ whiteSpace: 'pre-line' }}>
            {invoice.customer_memo}
          </div>
        </div>
      )}
      {invoice.private_note && (
        <div className="mb-3 flex-shrink-0">
          <div className="fw-semibold mb-1">Internal Note (not visible to customer)</div>
          <div className="p-3 border rounded" style={{ whiteSpace: 'pre-line', borderStyle: 'dashed' }}>
            <span className="text-muted">{invoice.private_note}</span>
          </div>
        </div>
      )}

      {/* Line items */}
      <Card className="d-flex flex-column flex-grow-1" style={{ minHeight: '150px', overflow: 'hidden' }}>
        <Card.Body className="flex-grow-1 overflow-auto p-0" style={{ minHeight: 0 }}>
          <DataTable
            data={lines}
            columns={lineColumns}
            getRowKey={(line: any) => line.line_id || line.line_num || ''}
            maxHeight="100%"
            enableStickyHeader={true}
            enableColumnReorder={false}
            enableColumnResize={true}
            enableColumnVisibility={false}
            emptyState={<div className="text-center text-muted py-3">No line items</div>}
          />
        </Card.Body>
      </Card>

      {/* Summary rows */}
      <Table className="mt-3 mb-0 flex-shrink-0" bordered size="sm">
        <tbody>
          <tr>
            <td className="text-end fw-semibold">Gross Amount</td>
            <td className="text-end fw-semibold" style={{ width: '120px' }}>{formatCurrency(invoice.invoice_amount)}</td>
          </tr>
          {hasDiscount && (
            <tr>
              <td className="text-end fw-bold">Discount</td>
              <td className="text-end text-danger fw-bold" style={{ width: '120px' }}>-{formatCurrency(invoice.discount_amount)}</td>
            </tr>
          )}
          <tr>
            <td className="text-end fw-semibold">Net Amount</td>
            <td className="text-end fw-semibold" style={{ width: '120px' }}>{formatCurrency(invoice.net_amount)}</td>
          </tr>
          <tr>
            <td className="text-end">
              Net Revenue
              <div className="text-muted small">(For commission purposes. Excludes taxes, shipping &amp; handling, customer returns, allowances, credits, refunds, discounts, and fees)</div>
            </td>
            <td className="text-end" style={{ width: '120px' }}>{formatCurrency(invoice.net_revenue)}</td>
          </tr>
          {payments.length > 0 && payments.map((payment: any, idx: number) => (
            <tr key={payment.id || idx}>
              <td className="text-end">Payment ({formatDate(payment.date)})</td>
              <td className="text-end text-success" style={{ width: '120px' }}>{formatCurrency(payment.amount)}</td>
            </tr>
          ))}
        </tbody>
      </Table>

      {/* Attachments */}
      <div className="flex-shrink-0">
        {invoice.id && <InvoiceAttachments invoiceId={invoice.id} />}
      </div>

      {/* Commission approval criteria */}
      <div className="mt-3 p-3 bg-body-tertiary border rounded flex-shrink-0">
        <div className="fw-semibold mb-2">Approvals</div>
        <div className="d-flex gap-4">
          <Form.Check
            id={`approval-valid-invoice-${invoice.id}`}
            type="checkbox"
            label="Valid Invoice"
            checked={invoice.has_valid_invoice || false}
            disabled={!canApprove}
            onChange={(e) => handleApprovalToggle('has_valid_invoice', e.target.checked)}
            title="Invoice has been reviewed and validated"
          />
          <div className="vr" />
          <Form.Check
            id={`approval-valid-po-${invoice.id}`}
            type="checkbox"
            label="Valid PO / Contract"
            checked={invoice.has_valid_po || false}
            disabled={!canApprove}
            onChange={(e) => handleApprovalToggle('has_valid_po', e.target.checked)}
            title="Valid purchase order or signed contract from customer"
          />
          <Form.Check
            id={`approval-vp-waiver-${invoice.id}`}
            type="checkbox"
            label="VP Waiver"
            checked={invoice.has_vp_waiver || false}
            disabled={!canApprove}
            onChange={(e) => handleApprovalToggle('has_vp_waiver', e.target.checked)}
            title="Written waiver from VP of Sales (alternative to PO)"
          />
        </div>
        <Form.Control
          as="textarea"
          rows={1}
          className="mt-2"
          placeholder="Approval notes..."
          defaultValue={invoice.approval_notes || ''}
          disabled={!canApprove}
          onBlur={(e) => {
            const newVal = e.target.value;
            if (newVal !== (invoice.approval_notes || '')) {
              handleApprovalToggle('approval_notes', newVal);
            }
          }}
          style={{ resize: 'vertical' }}
        />
      </div>

      {/* Status footer */}
      <div className="d-flex justify-content-end mt-3 flex-shrink-0">
        <div className="text-end">
          <div className="fw-bold mb-1">
            {statusBadge(invoice.status)}
            <span className="ms-2">Balance Due: {formatCurrency(invoice.balance_due)}</span>
          </div>
          {formatDate(invoice.due_date) && (
            <div className="text-muted small">Due Date: {formatDate(invoice.due_date)}</div>
          )}
        </div>
      </div>
    </div>
  );
};

// Attachments sub-component
const InvoiceAttachments: React.FC<{ invoiceId: string }> = ({ invoiceId }) => {
  const { data } = useQuery({
    queryKey: ['invoice-attachments', invoiceId],
    queryFn: () => getInvoicesInvoiceIdAttachments(invoiceId),
    enabled: !!invoiceId,
  });

  const attachments: InvoiceAttachment[] = (data as any)?.data || [];

  const downloadFn = useCallback(async (attachmentId: string): Promise<Blob> => {
    const rawBlob = await getInvoicesInvoiceIdAttachmentsAttachmentId(invoiceId, attachmentId);
    return rawBlob as unknown as Blob;
  }, [invoiceId]);

  return <AttachmentViewer attachments={attachments} downloadFn={downloadFn} />;
};

export default InvoiceDetailContent;
